import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const STATUS_CONFIG = {
  Active:   { color: "#15803d", bg: "rgba(34,197,94,0.12)",   label: "Active"   },
  Upcoming: { color: "#2563eb", bg: "rgba(59,130,246,0.12)",  label: "Upcoming" },
  Closed:   { color: "#6b7280", bg: "rgba(107,114,128,0.12)", label: "Closed"   },
  Draft:    { color: "#b45309", bg: "rgba(245,158,11,0.12)",  label: "Draft"    },
  Archived: { color: "#6b7280", bg: "rgba(107,114,128,0.09)", label: "Archived" },
};

const STATUS_FILTERS = ["All", "Active", "Upcoming", "Draft", "Closed", "Archived"];

const getStatusStyle = (status) =>
  STATUS_CONFIG[status] || { color: "#6b7280", bg: "rgba(107,114,128,0.12)", label: status };

const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const ElectionList = ({ elections, onSelectElection }) => {
  const [search, setSearch]             = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const filtered = useMemo(() => {
    let list = elections || [];
    if (statusFilter !== "All") {
      list = list.filter((e) => e.status === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((e) => e.title?.toLowerCase().includes(q));
    }
    return list;
  }, [elections, search, statusFilter]);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
            Elections
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b778c" }}>
            {filtered.length} of {(elections || []).length} elections
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }}>
          <TextField
            size="small"
            placeholder="Search by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#9eaab5" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: 200,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#fff",
              },
            }}
          />

          <ToggleButtonGroup
            value={statusFilter}
            exclusive
            onChange={(_, v) => { if (v !== null) setStatusFilter(v); }}
            size="small"
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.78rem",
                borderRadius: "8px !important",
                border: "1px solid rgba(47,99,173,0.14) !important",
                px: 1.25,
                py: 0.5,
                color: "#5f6b7a",
                mr: 0.5,
                mb: 0.5,
              },
              "& .Mui-selected": {
                backgroundColor: "rgba(47,99,173,0.10) !important",
                color: "#2f63ad !important",
              },
            }}
          >
            {STATUS_FILTERS.map((s) => (
              <ToggleButton key={s} value={s}>{s}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Box>

      {filtered.length === 0 ? (
        <Box
          sx={{
            py: 6,
            px: 2,
            textAlign: "center",
            borderRadius: "16px",
            border: "1px dashed rgba(47,99,173,0.14)",
            backgroundColor: "rgba(47,99,173,0.02)",
          }}
        >
          <Typography variant="body1" sx={{ color: "#6b778c", fontWeight: 500 }}>
            {(elections || []).length === 0
              ? "No elections created yet."
              : "No elections match your search."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <Table
            sx={{
              minWidth: 760,
              "& .MuiTableHead-root .MuiTableCell-root": {
                backgroundColor: "rgba(47,99,173,0.04)",
                color: "#223142",
                fontWeight: 700,
                borderBottom: "1px solid rgba(47,99,173,0.08)",
              },
              "& .MuiTableBody-root .MuiTableRow-root": {
                transition: "background-color 0.2s ease",
                cursor: "pointer",
              },
              "& .MuiTableBody-root .MuiTableRow-root:hover": {
                backgroundColor: "rgba(47,99,173,0.025)",
              },
              "& .MuiTableBody-root .MuiTableCell-root": {
                borderBottom: "1px solid rgba(47,99,173,0.06)",
                color: "#223142",
                py: 1.75,
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Start</TableCell>
                <TableCell>End</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Votes</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.map((e) => {
                const style = getStatusStyle(e.status);
                const voteCount = e.vote_count ?? e.total_votes ?? e.votes ?? null;

                return (
                  <TableRow key={e.id} onClick={() => onSelectElection(e)}>
                    <TableCell sx={{ fontWeight: 600 }}>{e.title}</TableCell>
                    <TableCell>{formatDate(e.start_datetime)}</TableCell>
                    <TableCell>{formatDate(e.end_datetime)}</TableCell>

                    <TableCell>
                      <Chip
                        label={style.label}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          borderRadius: "999px",
                          color: style.color,
                          backgroundColor: style.bg,
                        }}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: voteCount > 0 ? "#2f63ad" : "#9eaab5",
                          fontSize: "0.95rem",
                        }}
                      >
                        {voteCount !== null ? voteCount : "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={(ev) => { ev.stopPropagation(); onSelectElection(e); }}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          fontWeight: 700,
                          borderColor: "rgba(47,99,173,0.18)",
                          color: "#2f63ad",
                          "&:hover": {
                            borderColor: "#2f63ad",
                            backgroundColor: "rgba(47,99,173,0.04)",
                          },
                        }}
                      >
                        Open
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default ElectionList;
